'use client';

import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { MeSnapshot } from '../../state/MeSnapshot';
import MeSnapshotBlock from './MeSnapshotBlock';
import MeSnapshotTrendChart from './MeSnapshotTrendChart';
import Icon from '../Icon';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { useData } from '../../storage/accessors';
import useActualWheelId from '../../hooks/useActualWheelId';

interface MeSnapshotComparisonProps {
    initial: MeSnapshot | null;
    final: MeSnapshot | null;
    dateFrom?: string;
    dateTo?: string;
}

export default function MeSnapshotComparison({ initial, final, dateFrom, dateTo }: MeSnapshotComparisonProps) {
    const wheelId = useActualWheelId();
    const { meSnapshots } = useData();

    const allSnapshots = React.useMemo(() =>
        Object.values(meSnapshots.objects).filter((s: any) => s.wheelId === wheelId),
        [meSnapshots.objects, wheelId]
    );

    // Фильтруем снимки за период (от dateFrom до dateTo)
    const periodSnapshots = React.useMemo(() => {
        if (!dateFrom || !dateTo) return [];

        const filtered = allSnapshots
            .filter((s: any) => {
                const inPeriod = s.date >= dateFrom && s.date <= dateTo;
                return inPeriod;
            })
            .sort((a: any, b: any) => {
                const dateCompare = a.date.localeCompare(b.date);
                if (dateCompare !== 0) return dateCompare;
                // Если даты одинаковые, сортируем по timestamp (новые позже)
                return (b.timestamp || 0) - (a.timestamp || 0);
            });

        return filtered;
    }, [allSnapshots, dateFrom, dateTo]);

    if (!initial && !final) {
        return null;
    }

    return (
        <Row className="mb-4">
            <Col>
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center">
                            <Icon icon={faUser} className="me-2 text-secondary" style={{ fontSize: '1.5rem' }} />
                            <h4 className="mb-0">Я</h4>
                        </div>
                    </div>

                    {/* Кто я */}
                    <Row className="g-4 mb-4">
                        <Col lg={6}>
                            <div>
                                <small className="text-muted d-block mb-2">Было:</small>
                                <MeSnapshotBlock
                                    title="Кто я"
                                    text={initial?.whoAmI}
                                    rating={initial?.whoAmIRating || 0}
                                />
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div>
                                <small className="text-muted d-block mb-2">Стало:</small>
                                <MeSnapshotBlock
                                    title="Кто я"
                                    text={final?.whoAmI && final.whoAmI !== initial?.whoAmI ? final.whoAmI : undefined}
                                    rating={final?.whoAmIRating || 0}
                                    isFinal={true}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* Моя миссия */}
                    <Row className="g-4 mb-4">
                        <Col lg={6}>
                            <div>
                                <small className="text-muted d-block mb-2">Было:</small>
                                <MeSnapshotBlock
                                    title="Моя миссия"
                                    text={initial?.myMission}
                                    rating={initial?.myMissionRating || 0}
                                />
                            </div>
                        </Col>
                        <Col lg={6}>
                            <div>
                                <small className="text-muted d-block mb-2">Стало:</small>
                                <MeSnapshotBlock
                                    title="Моя миссия"
                                    text={final?.myMission && final.myMission !== initial?.myMission ? final.myMission : undefined}
                                    rating={final?.myMissionRating || 0}
                                    isFinal={true}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* График изменений */}
                    {periodSnapshots.length > 1 && (
                        <Row className="g-4">
                            <Col md={6}>
                                <h6 className="mb-3">Динамика "Кто я"</h6>
                                <MeSnapshotTrendChart
                                    snapshots={periodSnapshots}
                                    ratingType="whoAmI"
                                />
                            </Col>
                            <Col md={6}>
                                <h6 className="mb-3">Динамика "Моя миссия"</h6>
                                <MeSnapshotTrendChart
                                    snapshots={periodSnapshots}
                                    ratingType="myMission"
                                />
                            </Col>
                        </Row>
                    )}
                </div>
            </Col>
        </Row>
    );
}
